// ============================================================
// federationState.js
// Single source of truth for deriving the UI state from a
// TenantFederationConfig row (or its absence). The state machine
// is small enough to inline, but co-locating it here keeps the
// rendering surfaces dumb — the Tenant Federation tab and any
// other view that surfaces federation status route through this
// helper rather than re-deriving.
// ============================================================

/**
 * Federation UI state derived from a TenantFederationConfig row.
 *
 * Priority order matters when multiple conditions could be true:
 *   1. No row → 'not_configured'
 *   2. isActive === false → 'deactivated' (the operator's explicit off-switch;
 *      outranks consent state — a deactivated config is off regardless of
 *      whether consent was previously granted or has since been revoked, so
 *      showing "active" / "consent revoked" / "awaiting consent" would mislead)
 *   3. needsReconsent === true → 'needs_reconsent' (consent may still be set
 *      from a prior grant but is no longer valid)
 *   4. consentGrantedAt === null → 'pending_consent'
 *   5. otherwise → 'active'
 *
 * Note the strict `=== false`: only an explicitly-deactivated row triggers
 * 'deactivated'. A missing/undefined isActive (e.g. an older row shape) falls
 * through to the consent-derived states, preserving prior behaviour.
 *
 * @param {Object|null} config — TenantFederationConfig from the API, or
 *   null if no row exists for the Tenant.
 * @returns {'not_configured'|'deactivated'|'pending_consent'|'active'|'needs_reconsent'}
 */
export function deriveFederationState(config) {
  if (config == null) return 'not_configured';
  if (config.isActive === false) return 'deactivated';
  if (config.needsReconsent === true) return 'needs_reconsent';
  if (config.consentGrantedAt == null) return 'pending_consent';
  return 'active';
}
